// The MCP connect surface's strings (arc 101 slice 04, step 1) — the address a
// chat client is pointed at, and the two artifacts a headless one is configured
// with. Pure string work, deliberately not a component: what these snippets SAY
// is a contract with `apps/api` and with `packages/mcp`'s `.mcp.json`
// materializer, and a contract is worth testing without a DOM.
//
// THE ENDPOINT COMES FROM CONFIG, NOT A LITERAL. `PUBLIC_MCP_URL` is the origin
// `apps/api` publishes as the OAuth **resource identifier** in its RFC 9728
// discovery document (`apps/api/src/index.ts` `mcpBaseUrl` →
// `routes/oauth-metadata.ts`). A hosted connector fetches that document and
// refuses when the `resource` it names is not the URL it dialled — so a panel
// that printed `api.developerz.ai` while discovery published `mcp.` would hand
// every OAuth client an address that fails at consent. Vite inlines `PUBLIC_*`
// at build time (`vite.config.ts` `envPrefix`), and `infra/docker/dashboard.Dockerfile`
// carries the production value as the ARG default, next to `PUBLIC_API_URL`'s.
//
// UNSET ⇒ THE API ORIGIN, which is right exactly where it is reached. The SPA
// cannot import `@developerz/config` (it is a browser bundle — see
// `lib/donationModels.ts`), so "unset" has no boot gate behind it; the fallback
// therefore has to be a value that is CORRECT rather than a plausible guess.
// Locally it is: `config/development.yml` points both vars at the same
// `localhost:3001`, and a bare `vite dev` rides the `/v1` proxy on the page's
// own origin. In prod the fallback is unreachable, because the image build
// supplies the var. This is the same answer `mcpSnippet` already gives one file
// over in `lib/api-call.ts`, for the same reason — one MCP address in this app,
// not two that can disagree.
//
// NO CREDENTIAL EVER LANDS IN A SNIPPET. Both configured artifacts reference
// `$DEVELOPERZ_API_KEY` and never a token, matching `lib/api-call.ts`'s
// `TOKEN_ENV_VAR` (which is `packages/mcp`'s `PLATFORM_MCP_TOKEN_ENV`, the var
// every box already expands) and the shell-history-safe posture the docs take.
// The OAuth line needs no credential at all, which is why it leads.

import { apiOrigin, TOKEN_ENV_VAR } from './api-call';

/**
 * Origin the MCP endpoint is published at, inlined at build time. Blank/absent
 * is resolved by {@link mcpEndpoint}, never here — a module constant cannot see
 * `window.location`, and the fallback depends on it.
 */
const CONFIGURED_MCP_ORIGIN: string = import.meta.env['PUBLIC_MCP_URL'] ?? '';

/**
 * Name the server is registered under in the client's config. Matches
 * `PLATFORM_MCP_SERVER_KEY` (`packages/mcp/src/managed-servers.ts`), the key the
 * platform's own `.mcp.json` materializer uses on every box — so a maintainer
 * reading both sees one name. It is the reader's local alias, not a contract:
 * renaming it breaks nothing but the resemblance.
 */
export const MCP_SERVER_NAME = 'developerz';

/** The client walkthrough this panel is the short version of (`apps/marketing`). */
export const MCP_DOCS_URL = 'https://www.developerz.ai/docs/mcp-clients';

/**
 * The MCP endpoint to publish: `<origin>/v1/mcp`.
 *
 * Trailing slashes are stripped for the reason `apiOrigin` strips them — a
 * configured `https://mcp.example/` would otherwise yield `//v1/mcp`.
 */
export function mcpEndpoint(
  configured: string = CONFIGURED_MCP_ORIGIN,
  fallbackOrigin: string = apiOrigin(),
): string {
  const trimmed = configured.replace(/\/+$/, '');
  return `${trimmed === '' ? fallbackOrigin : trimmed}/v1/mcp`;
}

/**
 * The Claude Code registration line. `--transport http` is Streamable HTTP,
 * which is what `/v1/mcp` speaks; the header is the PAT path, so a client that
 * takes the OAuth connector above needs none of this.
 */
export function claudeMcpAddCommand(endpoint: string): string {
  return [
    `claude mcp add --transport http ${MCP_SERVER_NAME} ${endpoint}`,
    `  --header "Authorization: Bearer $${TOKEN_ENV_VAR}"`,
  ].join(' \\\n');
}

/**
 * The `.mcp.json` entry, byte-identical in SHAPE to what `platformMcpServer`
 * writes into every repo developerz.ai operates — `{type, url, headers}` under
 * `mcpServers.developerz`, with the bearer as a `${VAR}` reference the client
 * expands from the shell. A reader who has seen one recognises the other.
 */
export function mcpJsonSnippet(endpoint: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        [MCP_SERVER_NAME]: {
          type: 'http',
          url: endpoint,
          headers: { Authorization: `Bearer \${${TOKEN_ENV_VAR}}` },
        },
      },
    },
    null,
    2,
  );
}
